<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '500px'"
		@click:outside="closeDialog"
		@keydown.esc="closeDialog"
	>
		<v-card data-test="sig-cloud-storage-file-upload-dialog">
			<v-card-title class="dialog-header sgnl-subtitle-medium"
				>Choose files to upload
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="closeDialog"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />

			<v-card-text class="dialog-body">
				<v-form
					ref="mainForm"
					v-model="formValid"
					class="stack"
				>
					<div class="input-group">
						<div class="field-label">Destination</div>
						<v-text-field
							v-model="uploadDestination"
							placeholder="Upload destination"
							:rules="[rules.required, rules.noSpace, rules.maxSize, rules.noRestrictedCharacters]"
							data-test="upload-destination-input"
						/>
					</div>

					<div class="input-group">
						<div class="field-label">Files</div>
						<div
							class="file-select-area"
							:class="{ 'file-select-area--dragging': dragActive }"
							@click="triggerFileSelect"
							@dragenter.prevent="dragActive = true"
							@dragover.prevent="dragActive = true"
							@dragleave.prevent="dragActive = false"
							@drop.prevent="handleFileDrop"
							data-test="upload-file-input"
						>
							<template v-if="filesToUpload.length === 0">
								<div
									class="sgnl-flex sgnl-align-center file-select-placeholder sgnl-text-color-secondary sgnl-gap-8"
								>
									<CloudUpload16 />
									<span>Select files for upload</span>
								</div>
							</template>
							<template v-else>
								<div
									v-for="(file, index) in filesToUpload"
									:key="index"
									class="sgnl-flex sgnl-align-center sgnl-justify-space-between file-select-item sgnl-text-color-default"
								>
									<span class="sgnl-paragraph-s-regular">{{ file.name }}</span>
									<v-btn
										icon
										variant="text"
										size="x-small"
										@click.stop="removeFile(index)"
									>
										<Close16 />
									</v-btn>
								</div>
							</template>
						</div>
						<input
							ref="fileInput"
							type="file"
							multiple
							class="hidden-file-input"
							@change="handleFileSelect"
						/>
					</div>
				</v-form>
				<v-alert
					v-if="showError"
					:icon="false"
					border="start"
					class="sgnl-paragraph-s-regular"
					type="error"
					variant="outlined"
					elevation="0"
				>
					{{ errorMessage }}
				</v-alert>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="closeDialog"
					>Cancel</v-btn
				>
				<LimitableActionButton
					variant="flat"
					color="primary"
					:limits-to-check="[UserLimitsE.CloudStorageBytes]"
					:disabled="!formValid || filesToUpload.length === 0"
					data-test="upload-file-button"
					@valid-click="startUpload"
					btn-text="Upload"
				/>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import CloudUpload16 from "@carbon/icons-vue/es/cloud--upload/16";

// Components
import LimitableActionButton from "@/components/Common/LimitableActionButton.vue";

// Utilities
import * as dsUtil from "@/components/DataSources/utilities";

// Stores
import { useUploadsStore } from "@/stores/uploads";

// Types
import { UserLimitsE } from "@/js/tierconfig";

export default defineComponent({
	name: "SigCloudStorageFileUploadDialog",
	components: { LimitableActionButton, Close16, CloudUpload16 },
	emits: ["update:showDialog", "close-dialog"],
	props: {
		showDialog: { type: Boolean, default: false },
		initialUploadDestination: { type: String, default: undefined },
	},
	data: (): {
		filesToUpload: File[];
		uploadDestination: string;
		showError: boolean;
		errorMessage: string;
		formValid: boolean;
		dragActive: boolean;
	} => ({
		filesToUpload: [],
		uploadDestination: "",
		showError: false,
		errorMessage: "",
		formValid: false,
		dragActive: false,
	}),
	setup() {
		const uploadsStore = useUploadsStore();
		return { uploadsStore, UserLimitsE };
	},
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		rules(): {
			required: (value: string) => boolean | string;
			maxSize: (value: string) => boolean | string;
			noSpace: (value: string) => boolean | string;
			noRestrictedCharacters: (value: string) => boolean | string;
		} {
			return {
				required: (value: string) => !!value || "Required.",
				maxSize: (value: string) =>
					value.length < dsUtil.awsS3MaxPathSizeBytes ||
					"Directory path length should be less than 1024 Bytes",
				noSpace: (value: string) => value.indexOf(" ") == -1 || "Directory path cannot have spaces",
				noRestrictedCharacters: (value: string) => {
					return (
						dsUtil.awsS3RestrictedCharacters.every((character) => {
							return value.indexOf(character) == -1;
						}) ||
						`Directory path cannot contain ${dsUtil.awsS3RestrictedCharacters.reduce((t, c, idx) => {
							return idx == dsUtil.awsS3RestrictedCharacters.length - 1
								? t + " , or " + c
								: t + " , " + c;
						})}`
					);
				},
			};
		},
		allPathsLessThanMaxPathSize(): boolean {
			return this.filesToUpload.every((file) => {
				const uploadPath = this.uploadDestination ? `${this.uploadDestination}/${file.name}` : file.name;
				return this.rules.maxSize(uploadPath) == true;
			});
		},
		allPathsHaveNoSpaces(): boolean {
			return this.filesToUpload.every((file) => {
				const uploadPath = this.uploadDestination ? `${this.uploadDestination}/${file.name}` : file.name;
				return this.rules.noSpace(uploadPath) == true;
			});
		},
		allPathsHaveNoRestrictedCharacters(): boolean {
			return this.filesToUpload.every((file) => {
				const uploadPath = this.uploadDestination ? `${this.uploadDestination}/${file.name}` : file.name;
				return this.rules.noRestrictedCharacters(uploadPath) == true;
			});
		},
	},
	methods: {
		closeDialog() {
			this.showError = false;
			this.errorMessage = "";
			this.$emit("update:showDialog", false);
			this.$emit("close-dialog", false);
		},
		startUpload() {
			this.showError = false;
			if (!this.allPathsLessThanMaxPathSize) {
				this.errorMessage = `None of the file paths can be longer than ${dsUtil.awsS3MaxPathSizeBytes}B long.`;
				this.showError = true;
				return;
			} else if (!this.allPathsHaveNoSpaces) {
				this.errorMessage = `None of the file paths can contain spaces.`;
				this.showError = true;
				return;
			} else if (!this.allPathsHaveNoRestrictedCharacters) {
				this.errorMessage = `None of the file paths can contain ${dsUtil.awsS3RestrictedCharacters.reduce(
					(t, c, idx) => {
						return idx == dsUtil.awsS3RestrictedCharacters.length - 1 ? t + " , or " + c : t + " , " + c;
					},
				)}.`;
				this.showError = true;
				return;
			}
			this.uploadsStore.enqueue(this.filesToUpload, this.uploadDestination);
			this.closeDialog();
		},
		triggerFileSelect() {
			const input = this.$refs.fileInput as HTMLInputElement;
			input.value = "";
			input.click();
		},
		handleFileSelect(event: Event) {
			const input = event.target as HTMLInputElement;
			if (input.files) {
				this.filesToUpload = Array.from(input.files);
			}
		},
		handleFileDrop(event: DragEvent) {
			this.dragActive = false;
			const dropped = event.dataTransfer?.files;
			if (dropped && dropped.length > 0) {
				this.filesToUpload = [...this.filesToUpload, ...Array.from(dropped)];
			}
		},
		removeFile(index: number) {
			this.filesToUpload.splice(index, 1);
		},
	},
	mounted() {
		this.uploadDestination = this.initialUploadDestination ? this.initialUploadDestination : "";
	},
});
</script>
